<script setup lang="ts">
import { ref, watch } from 'vue'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { API, HOST } from '@/utils/http'

const props = defineProps<{
  open: boolean
  customerId: number
  currentEmail?: string
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'updated', newEmail: string): void
}>()

const email = ref(props.currentEmail ?? '')
const loading = ref(false)
const error = ref<string | null>(null)

watch(() => props.open, (open) => {
  if (open) {
    email.value = props.currentEmail ?? ''
    error.value = null
  }
})

const submit = async () => {
  if (!email.value.trim()) return
  loading.value = true
  error.value = null
  try {
    await API.patch(`${HOST}/payments/bridge/organizations/${props.customerId}/contact-email`, {
      email: email.value.trim(),
    })
    emit('updated', email.value.trim())
    emit('update:open', false)
  } catch (e: any) {
    error.value = e.response?.data?.detail || 'Failed to update email'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="sm:max-w-sm">
      <DialogHeader>
        <DialogTitle>Change Contact Email</DialogTitle>
      </DialogHeader>
      <div class="space-y-4 pt-2">
        <div class="space-y-1.5">
          <Label for="contact-email">New email address</Label>
          <Input
            id="contact-email"
            v-model="email"
            type="email"
            placeholder="contact@example.com"
            @keydown.enter="submit"
          />
        </div>
        <p v-if="error" class="text-xs text-destructive">{{ error }}</p>
        <div class="flex justify-end gap-2">
          <Button variant="outline" :disabled="loading" @click="emit('update:open', false)">Cancel</Button>
          <Button :disabled="loading || !email.trim()" @click="submit">
            {{ loading ? 'Saving…' : 'Save' }}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
